import JSZip from 'jszip';
import { v4 as uuid } from 'uuid';

import { db } from '@/lib/db';
import {
  CommentSchema,
  EditSchema,
  MessageSchema,
  ProjectSchema,
  TweakValueSchema,
  VersionSchema,
  type Comment,
  type Edit,
  type Message,
  type Project,
  type TweakValue,
  type Version,
} from '@/lib/schemas';

export const MANIFEST_VERSION = 1;

interface ProjectBundle {
  manifestVersion: typeof MANIFEST_VERSION;
  exportedAt: number;
  project: Project;
  versions: Version[];
  messages: Message[];
  edits: Edit[];
  comments: Comment[];
  tweakValues: TweakValue[];
}

async function loadBundle(projectId: string): Promise<ProjectBundle> {
  const project = await db().projects.get(projectId);
  if (!project) throw new Error(`Project ${projectId} not found.`);
  const [versions, messages, edits, comments, tweakValues] = await Promise.all([
    db().versions.where('projectId').equals(projectId).toArray(),
    db().messages.where('projectId').equals(projectId).toArray(),
    db().edits.where('projectId').equals(projectId).toArray(),
    db().comments.where('projectId').equals(projectId).toArray(),
    db().tweakValues.where('projectId').equals(projectId).toArray(),
  ]);
  return {
    manifestVersion: MANIFEST_VERSION,
    exportedAt: Date.now(),
    project,
    versions,
    messages,
    edits,
    comments,
    tweakValues,
  };
}

export async function exportProjectZip(projectId: string): Promise<Blob> {
  const bundle = await loadBundle(projectId);
  const zip = new JSZip();
  zip.file('project.json', JSON.stringify(bundle.project, null, 2));
  zip.file(
    'manifest.json',
    JSON.stringify(
      {
        manifestVersion: bundle.manifestVersion,
        exportedAt: bundle.exportedAt,
        projectId: bundle.project.id,
        counts: {
          versions: bundle.versions.length,
          messages: bundle.messages.length,
          edits: bundle.edits.length,
          comments: bundle.comments.length,
          tweakValues: bundle.tweakValues.length,
        },
      },
      null,
      2,
    ),
  );
  zip.file('messages.json', JSON.stringify(bundle.messages, null, 2));
  zip.file('edits.json', JSON.stringify(bundle.edits, null, 2));
  zip.file('comments.json', JSON.stringify(bundle.comments, null, 2));
  zip.file('tweakValues.json', JSON.stringify(bundle.tweakValues, null, 2));
  zip.file('versions.json', JSON.stringify(bundle.versions, null, 2));
  for (const v of bundle.versions) {
    if (v.htmlFinal) zip.file(`versions/${v.id}.html`, v.htmlFinal);
  }
  return zip.generateAsync({ type: 'blob' });
}

export async function importProjectZip(blob: Blob): Promise<string> {
  const zip = await JSZip.loadAsync(blob);
  const manifestStr = await zip.file('manifest.json')?.async('string');
  if (!manifestStr) throw new Error('Missing manifest.json in zip.');
  const projectStr = await zip.file('project.json')?.async('string');
  if (!projectStr) throw new Error('Missing project.json in zip.');

  const project = ProjectSchema.parse(JSON.parse(projectStr));
  const versions = VersionSchema.array().parse(JSON.parse((await zip.file('versions.json')!.async('string')) ?? '[]'));
  const messages = MessageSchema.array().parse(JSON.parse((await zip.file('messages.json')!.async('string')) ?? '[]'));
  const edits = EditSchema.array().parse(JSON.parse((await zip.file('edits.json')!.async('string')) ?? '[]'));
  const comments = CommentSchema.array().parse(JSON.parse((await zip.file('comments.json')!.async('string')) ?? '[]'));
  const tweakValues = TweakValueSchema.array().parse(JSON.parse((await zip.file('tweakValues.json')!.async('string')) ?? '[]'));

  // Re-id everything to avoid colliding with an existing project at the same id.
  const newProjectId = uuid();
  const idMap = new Map<string, string>([[project.id, newProjectId]]);
  const newProject: Project = {
    ...project,
    id: newProjectId,
    currentVersionId: project.currentVersionId,
    updatedAt: Date.now(),
  };
  const newVersions = versions.map((v) => {
    const newId = uuid();
    idMap.set(v.id, newId);
    return v;
  });
  // Second pass: now we know all version ids, remap.
  const remapped = {
    versions: newVersions.map((v) => ({
      ...v,
      id: idMap.get(v.id)!,
      projectId: newProjectId,
      parentVersionId: v.parentVersionId ? idMap.get(v.parentVersionId) ?? null : null,
      promptTurnId: v.promptTurnId,
    })),
    messages: messages.map((m) => ({
      ...m,
      id: uuid(),
      projectId: newProjectId,
      versionId: m.versionId ? idMap.get(m.versionId) ?? null : null,
    })),
    edits: edits
      .map((e) => ({
        ...e,
        id: uuid(),
        projectId: newProjectId,
        versionId: idMap.get(e.versionId) ?? '',
      }))
      .filter((e) => e.versionId),
    comments: comments
      .map((c) => ({
        ...c,
        id: uuid(),
        projectId: newProjectId,
        versionId: idMap.get(c.versionId) ?? '',
      }))
      .filter((c) => c.versionId),
    tweakValues: tweakValues
      .map((t) => ({
        ...t,
        id: uuid(),
        projectId: newProjectId,
        versionId: idMap.get(t.versionId) ?? '',
      }))
      .filter((t) => t.versionId),
  };
  newProject.currentVersionId = newProject.currentVersionId ? idMap.get(newProject.currentVersionId) ?? null : null;

  await db().transaction(
    'rw',
    [db().projects, db().versions, db().messages, db().edits, db().comments, db().tweakValues],
    async () => {
      await db().projects.put(newProject);
      await db().versions.bulkPut(remapped.versions);
      await db().messages.bulkPut(remapped.messages);
      await db().edits.bulkPut(remapped.edits);
      await db().comments.bulkPut(remapped.comments);
      await db().tweakValues.bulkPut(remapped.tweakValues);
    },
  );
  return newProjectId;
}
